import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { StatusCodes } from 'http-status-codes';
import { EMPTY, Observable, of } from 'rxjs';
import { catchError, mapTo, mergeMap, retry } from 'rxjs/operators';
import { DateZone } from 'wflow-cdk';
import { APP_ROUTES } from '../../routing.const';
import { DowntimeAPIService } from '../api/api/downtime/downtime.service';

@Injectable()
export class MaintenanceInterceptor implements HttpInterceptor {
  private static _caught$: Observable<HttpEvent<any>> | null = null;

  /**
   * Checks the API's maintenance status by analyzing the last error encountered.
   * @returns {Observable<boolean>} An observable emitting `true` if the API is under maintenance, otherwise `false`.
   */
  static checkMaintenanceStatus(): Observable<boolean> {
    if (this._caught$ == null) {
      return of(false);
    }

    return this._caught$.pipe(
      retry(1),
      mapTo(false),
      catchError((err) => of(!MaintenanceInterceptor._isMaintenanceError(err))),
    );
  }

  constructor(
    private _router: Router,
    private downtimeSvc: DowntimeAPIService,
  ) {}

  /**
   * Intercepts HTTP requests to handle maintenance status based on API response errors.
   * @param {HttpRequest<any>} req The outgoing HTTP request.
   * @param {HttpHandler} next The next handler in the chain.
   * @returns {Observable<HttpEvent<any>>} The processed HTTP event or a redirection in case of maintenance.
   */
  intercept(
    req: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    let retryCount = 2;

    return next.handle(req).pipe(
      catchError((error, caught) => {
        // Retry the request in case of a GATEWAY_TIMEOUT error
        if (error.status === StatusCodes.GATEWAY_TIMEOUT && retryCount > 0) {
          retryCount--;
          return caught.pipe(retry(1));
        }

        // Handle API downtime and redirect to maintenance page if necessary
        if (error?.status === 0) {
          return this._handleDowntimeError(error, caught);
        }

        // If the error indicates maintenance, redirect to the maintenance page
        if (MaintenanceInterceptor._isMaintenanceError(error)) {
          return this._redirectToMaintenance(caught);
        }

        throw error; // Re-throw any other errors
      }),
    );
  }

  /**
   * Handles downtime errors by checking downtime info and determining if maintenance is active.
   * @param {any} error The error object returned by the HTTP request.
   * @param {Observable<any>} caught The caught HTTP request observable.
   * @returns {Observable<never>} An observable that navigates to the maintenance page or rethrows the error.
   * @private
   */
  private _handleDowntimeError(
    error: any,
    caught: Observable<HttpEvent<any>>,
  ): Observable<never> {
    return this.downtimeSvc.getDowntimeInfo().pipe(
      mergeMap((downtime) => {
        if (!downtime) {
          throw error;
        }

        const from = DateZone.toLocal(downtime.timeFrom).valueOf();
        const to = DateZone.toLocal(downtime.timeTo).valueOf();
        const now = Date.now();

        if (now >= from && now <= to) {
          return this._redirectToMaintenance(caught);
        }

        throw error;
      }),
    );
  }

  /**
   * Redirects the user to the maintenance page when a maintenance error occurs.
   * @param {Observable<any>} caught The caught HTTP request observable.
   * @returns {Observable<never>} An observable that navigates to the maintenance page.
   * @private
   */
  private _redirectToMaintenance(
    caught: Observable<HttpEvent<any>>,
  ): Observable<never> {
    MaintenanceInterceptor._caught$ = caught;
    this._router.navigate([APP_ROUTES.MAINTENANCE]);
    return EMPTY;
  }

  /**
   * Determines if the given error status code corresponds to a maintenance scenario.
   * @param {any} error The error object returned by the HTTP request.
   * @returns {boolean} `true` if the error indicates maintenance, otherwise `false`.
   */
  private static _isMaintenanceError(error: any): boolean {
    switch (error?.status) {
      case StatusCodes.BAD_GATEWAY:
      case StatusCodes.SERVICE_UNAVAILABLE:
      case StatusCodes.GATEWAY_TIMEOUT:
        return true;
      default:
        return false;
    }
  }
}
